import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import {
  Roles,
  StepStatus,
  type ReducedOrganization,
} from '../../Types/interfaces';
import {
  GetInternshipOfferApplications,
  GetInternshipOffersOfOrganization,
  GetInternshipProcessesByOrganizations,
  GetStudentsAppliedToInternshipOffer,
} from '../../helper/apiHelper';
import { useHasRole } from '../useHasRole';

export const useApplicationCount = (
  userOrganizations: Array<ReducedOrganization> | null
) => {
  return useQuery<number>({
    queryKey: [...QUERY_KEYS.APPLICATION_COUNT, userOrganizations],
    queryFn: async () => {
      const organizationDataPromises = userOrganizations?.map(async (org) => {
        const response = await GetInternshipOfferApplications(
          org.id.toString()
        );
        return response.length;
      });
      const organizationData = await Promise.all(
        organizationDataPromises ?? []
      );
      const count = organizationData.reduce(
        (total, current) => total + current,
        0
      );

      return count;
    },

    enabled: !!userOrganizations,
  });
};

export const useStudentCount = (
  userOrganizations: Array<ReducedOrganization> | null
) => {
  return useQuery<number>({
    queryKey: [...QUERY_KEYS.STUDENT_COUNT, userOrganizations],
    queryFn: async () => {
      const internshipPromises = userOrganizations?.map(async (org) => {
        return await GetInternshipOffersOfOrganization(org.id.toString());
      });

      const internships = (await Promise.all(internshipPromises ?? []))
        .filter((internship) => internship.length !== 0)
        .flat(1);

      const applicationPromises = internships.map(async (internship) => {
        return await GetStudentsAppliedToInternshipOffer(
          internship.organizationId.toString(),
          internship.id.toString()
        );
      });

      const applications = (await Promise.all(applicationPromises))
        .filter((application) => application.length !== 0)
        .flat(1);

      return applications.length;
    },
    enabled: !!userOrganizations,
  });
};

export const useConventionsToBeSignedCount = (
  userOrganizations: Array<ReducedOrganization> | null
) => {
  const hasRole = useHasRole();
  const isAuthorized =
    hasRole(Roles.Administrator) || hasRole(Roles.OrganizationRepresentative);

  return useQuery<number>({
    queryKey: [...QUERY_KEYS.CONVENTIONS_TO_BE_SIGNED_COUNT, userOrganizations],
    queryFn: async () => {
      const processesResp = await GetInternshipProcessesByOrganizations(
        userOrganizations?.map((org) => org.id) ?? []
      );
      const stepName = 'SigningInternshipConventionByOrganization';

      const toBeSignedProcesses = processesResp.filter((process) => {
        const step = process.processSteps.find(
          (step) => step.stepName === stepName
        );
        const stepStatus = step ? step.stepStatus : StepStatus.notStarted;

        if (stepStatus === StepStatus.initialized) {
          return true;
        }
        return false;
      });

      return toBeSignedProcesses.length;
    },
    enabled: !!userOrganizations && isAuthorized,
  });
};
